import { useEffect, useRef } from "react";
import useInView from "../hooks/useInView";

/**
 * Renders one image, video or Vimeo embed from `src/data/projects.js`.
 *
 * Videos are silent, looping clips (like GIFs). To save battery and data
 * they only play while on screen — or, if you pass `playing`, only while
 * `playing` is true (used by the index cards, which play on hover).
 *
 * @param {object}  props
 * @param {{src, alt, type}} props.item   A `media()` object from projects.js.
 * @param {string}  [props.className]
 * @param {boolean} [props.playing]       Force play/pause. Omit = auto.
 * @param {boolean} [props.eager]         Load straight away (skip lazy-load).
 * @param {function} [props.onRatio]      Called with width / height once
 *                                        known (used by the gallery layout).
 */
export default function Media({
  item,
  className = "",
  playing,
  eager = false,
  onRatio,
}) {
  const ref = useRef(null);
  const inView = useInView(ref, "200px");

  // Decide whether the video should be playing right now.
  const shouldPlay = playing === undefined ? inView : playing;

  useEffect(() => {
    const video = ref.current;
    if (!video || item.type !== "video") return;
    if (shouldPlay) {
      // play() returns a promise that rejects if the browser blocks it —
      // harmless for muted clips, so we just ignore the error.
      // (Older browsers return nothing, hence the `?.`.)
      video.play()?.catch(() => {});
    } else {
      video.pause();
    }
  }, [shouldPlay, item.type]);

  // Vimeo: an embedded player, sized to the video's aspect ratio.
  // (`playing` doesn't apply — visitors press play themselves.)
  // Report the picture's shape (width / height) once the browser knows it.
  // Images that loaded before React attached `onLoad` are caught here too.
  useEffect(() => {
    if (!onRatio) return;
    const el = ref.current;
    if (item.type === "vimeo") onRatio(item.ratio);
    else if (item.type === "image" && el?.complete && el.naturalWidth)
      onRatio(el.naturalWidth / el.naturalHeight);
  }, [item, onRatio]);

  const reportImage = (e) =>
    onRatio?.(e.currentTarget.naturalWidth / e.currentTarget.naturalHeight);
  const reportVideo = (e) =>
    onRatio?.(e.currentTarget.videoWidth / e.currentTarget.videoHeight);

  if (item.type === "vimeo") {
    return (
      <div
        ref={ref}
        className={`media media--embed ${className}`}
        style={{ aspectRatio: item.ratio }}
      >
        <iframe
          src={item.src}
          title={item.alt || "Vimeo video"}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          loading={eager ? "eager" : "lazy"}
        />
      </div>
    );
  }

  if (item.type === "video") {
    return (
      <video
        ref={ref}
        className={`media ${className}`}
        src={item.src}
        aria-label={item.alt || undefined}
        muted
        loop
        playsInline
        preload={eager ? "auto" : "metadata"}
        onLoadedMetadata={reportVideo}
      />
    );
  }

  return (
    <img
      ref={ref}
      className={`media ${className}`}
      src={item.src}
      alt={item.alt}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      onLoad={reportImage}
    />
  );
}
